import type { MobileID } from 'smsaero-mobileid-sdk'

import { AbortError } from '@app/errors/AbortError'

export const SMSAERO_TOKEN_URL = '/api/v3/auth/phone/smsaero/token'

export type SmsaeroVerifiedResult = {
  session_id: string
  verify_token: string
  /** Device fingerprint hash from the SDK — sent to the backend for the verification audit. */
  fingerprint_hash: string | null
}

export type SmsaeroFailReason = 'rejected' | 'expired' | 'rate_limit' | 'error'

export type SmsaeroVerifierEvents = {
  /** The cascade reached the SMS code — show the input field. */
  onOtpRequired: () => void
  /** Verification passed — terminal event. */
  onVerified: (result: SmsaeroVerifiedResult) => void
  /** Wrong OTP code — can be re-entered. */
  onInvalidCode: (message: string) => void
  /** Terminal rejection/error — the caller decides where to fall back. */
  onFailed: (reason: SmsaeroFailReason) => void
}

export interface SmsaeroVerifierInterface {
  start: (phone: string, events: SmsaeroVerifierEvents) => Promise<void>
  submitOTP: (code: string) => Promise<void>
  /** Restart the verification cycle with the same number (resend the code). */
  retry: () => Promise<void>
  destroy: () => void
}

/**
 * Wrapper around the headless SMS Aero «Мобильная авторизация» (MobileID) SDK.
 * The SDK fetches an init token from our backend (tokenUrl) and runs the
 * Seamless → SIM-PUSH → SMS-code cascade; the result (`verify_token` +
 * `session_id`) is confirmed by the backend server-to-server via
 * `POST /api/v3/auth/login` (method=smsaero) or `POST /api/v3/auth/phone/bind`.
 *
 * Single-use: a repeated `start()` throws — spin up a new instance per verification.
 */
export class SmsaeroVerifier implements SmsaeroVerifierInterface {
  private started = false
  private destroyed = false
  private mid: MobileID | null = null
  // Settles when the session is up (resolve), when start() failed (reject), or when the
  // verifier was destroyed (resolve) — gates submitOTP/retry so a call made while the SDK
  // is still loading is queued rather than silently dropped.
  private loadedChannel = Promise.withResolvers<void>()

  constructor() {
    // start() may reject the latch with no submitOTP/retry ever awaiting it (the caller
    // fell back to SMS); absorb it here so it never surfaces as an unhandled rejection.
    void this.loadedChannel.promise.catch(() => {})
  }

  async start(phone: string, events: SmsaeroVerifierEvents) {
    if (this.started) throw new Error('SmsaeroVerifier: start() called on an already-started verifier')
    this.started = true

    try {
      const { MobileID } = await import('smsaero-mobileid-sdk')
      // Destroyed while the SDK was loading (caller unmounted) — abort instead of spinning up a dead session.
      if (this.destroyed) throw new AbortError('SmsaeroVerifier: destroyed before start() finished loading')
      const instance = new MobileID({ tokenUrl: SMSAERO_TOKEN_URL })
      // Set before init() so destroy() can tear the instance down even if the cascade below throws.
      this.mid = instance

      instance.on('otp_required', () => events.onOtpRequired())
      instance.on('verified', data => {
        const sessionId = instance.getSessionId()
        if (!data.verify_token || !sessionId) {
          events.onFailed('error')
          return
        }
        events.onVerified({ session_id: sessionId, verify_token: data.verify_token, fingerprint_hash: instance.getFingerprintHash() })
      })
      instance.on('invalid_code', data => events.onInvalidCode(data.message))
      instance.on('rejected', () => events.onFailed('rejected'))
      instance.on('expired', () => events.onFailed('expired'))
      instance.on('rate_limit', () => events.onFailed('rate_limit'))
      instance.on('error', () => events.onFailed('error'))

      await instance.init()
      await instance.start(phone)
      this.loadedChannel.resolve()
    } catch (error) {
      // Tear down the half-initialized instance and fail the latch; re-throw so the caller can fall back.
      this.mid?.destroy()
      this.mid = null
      this.loadedChannel.reject(error)
      throw error
    }
  }

  async submitOTP(code: string) {
    const mid = await this.requireSession()
    await mid.submitOTP(code)
  }

  async retry() {
    const mid = await this.requireSession()
    await mid.silentRetry()
  }

  destroy() {
    this.destroyed = true
    this.mid?.destroy()
    this.mid = null
    this.loadedChannel.resolve()
  }

  private async requireSession(): Promise<MobileID> {
    await this.loadedChannel.promise
    if (!this.mid) throw new Error('SmsaeroVerifier: no active session — the verifier was destroyed or never started')
    return this.mid
  }
}

export const createSmsaeroVerifier = (): SmsaeroVerifierInterface => new SmsaeroVerifier()
